import { gql } from '@grandvisionhq/graphql';

const billingAddressFragment = gql`
  fragment OrderBillingAddressFragment on OrderAddress {
    additionalStreetInfo
    city
    countryCode
    email
    firstName
    lastName
    phone
    postalCode
    region
    streetName
    streetNumber
  }
`;
const shippingAddressFragment = gql`
  fragment OrderShippingAddressFragment on OrderAddress {
    additionalStreetInfo
    city
    countryCode
    email
    externalId
    firstName
    lastName
    phone
    postalCode
    region
    streetName
    streetNumber
  }
`;
const priceFragment = gql`
  fragment OrderPriceFragment on OrderPrice {
    currencyCode
    discountedAmount
    paymentMethodPrice
    shippingPrice
    subtotal
    total
  }
`;
const lensFragment = gql`
  fragment LensFragment on PrescriptionOpticalParametersGlasses {
    pupillaryDistance
    prism
    power
    cylinder
    axis
    addition
  }
`;
const orderPrescriptionFragment = gql`
  fragment OrderPrescriptionFragment on PrescriptionGlassPrescription {
    ... on AddedGlassesPrescription {
      leftLens {
        ...LensFragment
      }
      rightLens {
        ...LensFragment
      }
    }
    ... on EmailPrescription {
      message
    }
    ... on ExistingPrescription {
      message
    }
    ... on FilePrescription {
      prescriptionFileName
      pupillaryDistanceFileName
    }
  }
  ${lensFragment}
`;
const solutionLineItemFragment = gql`
  fragment SolutionLineItemFragment on OrderLineItemSolution {
    price {
      productPrice
      totalPaid
    }
    quantity
    product {
      productType
      brand
      mainImage
      modelCode
      name {
        locale
        value
      }
      sku
      currentProduct {
        published
        slug
      }
      volume
      volumeUnit
    }
  }
`;
const bundledLineItemsFragment = gql`
  fragment BundledLineItemsFragment on OrderedBundledLineItem {
    ... on OrderLineItemContactLens {
      price {
        productPrice
        totalPaid
      }
      quantity
      eyeSide
      prescription {
        addition
        axis
        baseCurve
        cylinder
        dominance
        diameter
        power
      }
      product {
        brand
        mainImage
        modelCode
        name {
          locale
          value
        }
        sku
        currentProduct {
          published
        }
        # 09.06.22: boxQuantity is disabled explicitly because it breaks the validation on
        # GraphQL side. This property is going to be deprecated from the graph, but is kept for documentation
        # boxQuantity
        replacementFrequency {
          locale
          value
        }
      }
    }
    ... on OrderLineItemSolution {
      ...SolutionLineItemFragment
    }
  }
  ${solutionLineItemFragment}
`;
const contactLensLineItemFragment = gql`
  fragment ContactLensLineItemFragment on OrderLineItemContactLens {
    price {
      productPrice
      totalPaid
    }
    quantity
    eyeSide
    prescription {
      addition
      axis
      baseCurve
      cylinder
      dominance
      diameter
      power
    }
    product {
      productType
      brand
      mainImage
      modelCode
      name {
        locale
        value
      }
      sku
      currentProduct {
        published
        slug
      }
      boxQuantity
      replacementFrequency {
        locale
        value
      }
    }
  }
`;
const descriptionFragment = gql`
  fragment DescriptionFragment on LocalizedString {
    locale
    value
  }
`;
const configurationDetailsFragment = gql`
  fragment ConfigurationDetailsFragment on PrescriptionGlassConfigurationDetails {
    configurationType
    description {
      ...DescriptionFragment
    }
    productPrice
    totalPaid
  }
  ${descriptionFragment}
`;
const prescriptionGlassLineItemFragment = gql`
  fragment PrescriptionGlassLineItemFragment on OrderLineItemPrescriptionGlass {
    price {
      productPrice
      totalPaid
    }
    quantity
    configurationDetails {
      ...ConfigurationDetailsFragment
    }
    prescription {
      ...OrderPrescriptionFragment
    }
    product {
      productType
      brand
      mainImage
      modelCode
      name {
        locale
        value
      }
      sku
      currentProduct {
        published
        slug
      }
      productPrice
      totalPaid
    }
  }
  ${configurationDetailsFragment}
  ${orderPrescriptionFragment}
`;
const nonPrescriptionBundleOrderLineItemFragment = gql`
  fragment NonPrescriptionBundleLineItemFragment on OrderLineItemNonPrescriptionBundle {
    quantity
    price {
      totalPaid
      productPrice
    }
    product {
      productType
      sku
      currentProduct {
        published
        slug
      }
      name {
        locale
        value
      }
      brand
      modelCode
      mainImage
      productPrice
      totalPaid
    }
    configurationDetails {
      configurationType
      description {
        locale
        value
      }
      totalPaid
      productPrice
    }
  }
`;
const contactLensBundleLineItemFragment = gql`
  fragment ContactLensBundleLineItemFragment on OrderLineItemBundle {
    price {
      productPrice
      totalPaid
    }
    quantity
    product {
      productType
      sku
      currentProduct {
        published
        slug
      }
      name {
        locale
        value
      }
      brand
      modelCode
      mainImage
      bundledProductName {
        locale
        value
      }
      lensType {
        locale
        value
      }
    }
    bundledLineItems {
      ...BundledLineItemsFragment
    }
  }
  ${bundledLineItemsFragment}
`;
const regularLineItemFragment = gql`
  fragment RegularLineItemFragment on OrderLineItemRegular {
    # TODO: price and quantity are always the same on all line items, so maybe we can save some code duplication here
    price {
      productPrice
      totalPaid
    }
    quantity
    product {
      productType
      brand
      mainImage
      modelCode
      name {
        locale
        value
      }
      sku
      currentProduct {
        published
        slug
      }
    }
  }
`;
const orderLineItemsFragment = gql`
  fragment OrderLineItemsFragment on OrderLineItem {
    ... on OrderLineItemRegular {
      ...RegularLineItemFragment
    }
    ... on OrderLineItemContactLens {
      ...ContactLensLineItemFragment
    }
    ... on OrderLineItemPrescriptionGlass {
      ...PrescriptionGlassLineItemFragment
    }
    ... on OrderLineItemSolution {
      ...SolutionLineItemFragment
    }
    ... on OrderLineItemBundle {
      ...ContactLensBundleLineItemFragment
    }
    ... on OrderLineItemNonPrescriptionBundle {
      ...NonPrescriptionBundleLineItemFragment
    }
  }
  ${contactLensLineItemFragment}
  ${prescriptionGlassLineItemFragment}
  ${solutionLineItemFragment}
  ${contactLensBundleLineItemFragment}
  ${nonPrescriptionBundleOrderLineItemFragment}
  ${regularLineItemFragment}
`;
const orderFragment = gql`
  fragment OrderFragment on Order {
    billingAddress {
      ...OrderBillingAddressFragment
    }
    orderDate
    orderNumber
    paymentMethod
    paymentStatus
    returnStatus
    state {
      key
      title {
        locale
        value
      }
    }
    receiptUrl
    shippingAddress {
      ...OrderShippingAddressFragment
    }
    deliveryMethod
    price {
      ...OrderPriceFragment
    }
    shipmentStatus
    shipmentTrackingURL
    shippingInfo {
      deliveryMethodName
    }
    lineItems {
      ...OrderLineItemsFragment
    }
  }
  ${billingAddressFragment}
  ${shippingAddressFragment}
  ${priceFragment}
  ${orderLineItemsFragment}
`;
const orderResultsFragment = gql`
  fragment OrderResultsFragment on Order {
    orderDate
    orderNumber
    paymentStatus
    state {
      key
      title {
        locale
        value
      }
    }
    shippingInfo {
      deliveryMethodName
    }
    shipmentStatus
    lineItems {
      ...OrderLineItemsFragment
    }
  }
  ${orderLineItemsFragment}
`;

export { billingAddressFragment, bundledLineItemsFragment, configurationDetailsFragment, contactLensBundleLineItemFragment, contactLensLineItemFragment, descriptionFragment, lensFragment, nonPrescriptionBundleOrderLineItemFragment, orderFragment, orderLineItemsFragment, orderPrescriptionFragment, orderResultsFragment, prescriptionGlassLineItemFragment, priceFragment, regularLineItemFragment, shippingAddressFragment, solutionLineItemFragment };
//# sourceMappingURL=line-item.js.map
